import React, { useState } from "react";
import { Box, Button, TextField, Typography, Modal, IconButton, Card, CardContent, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon } from "@mui/icons-material";
import SearchPatient from "./SearchPatient";
import { DateField } from "@mui/x-date-pickers/DateField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MuiTelInput } from "mui-tel-input";
import { SERVER_BASE_URL } from "../Config";
import dayjs, { Dayjs } from "dayjs";
import PatientAddressManager from "./PatientAddressManager";
import { joinPatientName, splitPatientName } from "./patientName";

const PatientManagement: React.FC = () => {
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patientTelephone, setPatientTelephone] = useState("");
  const [patientId, setPatientId] = useState<number | null>(null);
  const [patientLastName, setPatientLastName] = useState("");
  const [patientFirstName, setPatientFirstName] = useState("");
  const [patientDob, setPatientDob] = useState<Dayjs | null>(null);
  const [patientEmail, setPatientEmail] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState("");

  const handleNewPatientClick = () => {
    // Reset form for new patient
    setPatientDob(null);
    setPatientTelephone("");
    setPatientId(null);
    setPatientLastName("");
    setPatientFirstName("");
    setPatientEmail("");
    setIsEditing(false);
    setShowPatientModal(true);
  };

  const handleExistingPatientSelect = (patient: any) => {
    // Set selected patient without opening the edit modal
    setSelectedPatient(patient);
  };

  const handleEditPatientClick = () => {
    if (selectedPatient) {
      // Load patient data into form for editing
      const { firstName, lastName } = splitPatientName(selectedPatient.name);
      setPatientId(selectedPatient.id);
      setPatientFirstName(firstName);
      setPatientLastName(lastName);
      if (selectedPatient.dob) {
        setPatientDob(dayjs(selectedPatient.dob));
      }
      setPatientEmail(selectedPatient.email);
      setPatientTelephone(selectedPatient.phone);
      setIsEditing(true);
      setShowPatientModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowPatientModal(false);
    setErrorMessage("");
  };

  const handleCreateOrUpdatePatient = async (closeOnCreate: boolean = false, fromAddressManager: boolean = false) => {
    // Clear any previous error messages
    setErrorMessage("");

    // Validation for required fields
    if (!patientLastName.trim()) {
      setErrorMessage("Patient last name is required");
      return false;
    }

    if (!patientFirstName.trim()) {
      setErrorMessage("Patient first name is required");
      return false;
    }

    if (!patientTelephone.trim()) {
      setErrorMessage("Patient phone number is required");
      return false;
    }

    if (!patientDob) {
      setErrorMessage("Patient date of birth is required");
      return false;
    }

    const providerToken = localStorage.getItem("providerToken");
    let url = `${SERVER_BASE_URL}/patient`;
    let method = "POST";

    let editMode = false;
    if (isEditing && patientId) {
      editMode = true;
      url = `${SERVER_BASE_URL}/patient/${patientId}`;
      method = "PUT";
    }

    const fullName = joinPatientName(patientFirstName, patientLastName);
    const body = {
      firstName: patientFirstName.trim(),
      lastName: patientLastName.trim(),
      name: fullName, // Server rebuilds this from the parts; sent for compatibility
      dob: patientDob ? patientDob.format("YYYY-MM-DD") : null,
      email: patientEmail,
      phone: patientTelephone
    };

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${providerToken}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      setPatientId(data.id);

      // Update the selected patient with the new data
      const updatedPatient = {
        id: data.id,
        name: fullName,
        dob: patientDob ? patientDob.format("YYYY-MM-DD") : null,
        email: patientEmail,
        phone: patientTelephone
      };

      setSelectedPatient(updatedPatient);
      setIsEditing(true);

      if (editMode) {
        handleCloseModal();
      }
      return data.id;
    } catch (error) {
      console.error("Error creating or updating patient:", error);
      setErrorMessage("Failed to create or update patient. Please try again.");
      return false;
    }
  };

  const handleCreateOrUpdatePatientWrapper = async () => {
    const success = await handleCreateOrUpdatePatient(true, false);
    if (success !== false) {
      setShowPatientModal(false);
    }
  };

  const handleCreatePatientForAddress = async () => {
    const success = await handleCreateOrUpdatePatient(false, true);
    return success;
  };

  const handleDeletePatient = async () => {
    if (!selectedPatient) return;

    try {
      const providerToken = localStorage.getItem("providerToken");
      const response = await fetch(`${SERVER_BASE_URL}/patient/${selectedPatient.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${providerToken}`
        }
      });

      if (response.ok) {
        setDeleteSuccess("Patient deleted successfully");
        setSelectedPatient(null);
        setShowDeleteDialog(false);
        // Clear success message after 3 seconds
        setTimeout(() => setDeleteSuccess(""), 3000);
      } else {
        const data = await response.json();
        setErrorMessage(data.message || "Failed to delete patient");
        setShowDeleteDialog(false);
      }
    } catch (error) {
      console.error("Error deleting patient:", error);
      setErrorMessage("An error occurred while deleting the patient");
      setShowDeleteDialog(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Manage Patients
      </Typography>

      {deleteSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {deleteSuccess}
        </Alert>
      )}

      {errorMessage && !showPatientModal && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}

      <SearchPatient onSelect={handleExistingPatientSelect} />

      <Button
        variant="contained"
        color="primary"
        onClick={handleNewPatientClick}
        sx={{ width: "100%", mt: 1 }}
      >
        + New Patient
      </Button>

      {selectedPatient && (
        <Card sx={{ mt: 2, p: 2 }}>
          <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="body1">
                <strong>Name:</strong> {selectedPatient.name}
              </Typography>
              <Typography variant="body2">
                <strong>DOB:</strong> {selectedPatient.dob}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong> {selectedPatient.phone}
              </Typography>
              {selectedPatient.email && (
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedPatient.email}
                </Typography>
              )}
            </Box>
            <Box>
              <IconButton onClick={handleEditPatientClick} color="primary">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => setShowDeleteDialog(true)} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}

      <Modal
        open={showPatientModal}
        onClose={handleCloseModal}
        aria-labelledby="patient-modal"
        aria-describedby="modal-to-create-or-edit-patient"
        sx={{ zIndex: 1400 }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 8,
              right: 8
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2" gutterBottom>
            {isEditing ? "Edit Patient" : "Create New Patient"}
          </Typography>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <TextField
            label="Last Name"
            variant="outlined"
            required
            fullWidth
            sx={{ mb: 2 }}
            value={patientLastName}
            onChange={(e) => {
              setPatientLastName(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
          />
          <TextField
            label="First Name"
            variant="outlined"
            required
            fullWidth
            sx={{ mb: 2 }}
            value={patientFirstName}
            onChange={(e) => {
              setPatientFirstName(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateField
              label="Date of Birth"
              fullWidth
              sx={{ mb: 2 }}
              value={patientDob}
              onChange={(newValue) => {
                setPatientDob(newValue);
                if (errorMessage) setErrorMessage("");
              }}
            />
          </LocalizationProvider>
          <TextField
            label="Email (Optional)"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={patientEmail}
            onChange={(e) => {
              setPatientEmail(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
          />
          <MuiTelInput
            label="Phone"
            fullWidth
            value={patientTelephone}
            onChange={(value) => {
              setPatientTelephone(value);
              if (errorMessage) setErrorMessage("");
            }}
            defaultCountry={"US"}
            disableDropdown
          />
          <Box sx={{ mb: 2 }} />

          {/* Show PatientAddressManager when creating or editing a patient */}
          <PatientAddressManager
            patientId={patientId || null}
            setPatientId={setPatientId}
            createPatientFn={handleCreatePatientForAddress}
          />

          <br />
          <Button variant="contained" color="primary" onClick={handleCreateOrUpdatePatientWrapper} fullWidth>
            {isEditing ? "Update Patient" : "Create Patient"}
          </Button>
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle>Delete Patient</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedPatient?.name}? This action cannot be undone.
            The patient will be archived and will no longer appear in searches.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeletePatient} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientManagement;
